import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSavedFilterMutations } from '@/lib/db/rxdb/hooks'

interface RenameFilterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filterId: string
  currentName: string
}

export function RenameFilterDialog({
  open,
  onOpenChange,
  filterId,
  currentName,
}: RenameFilterDialogProps) {
  const { t } = useTranslation('tasks')
  const { t: tCommon } = useTranslation('common')
  const [name, setName] = useState(currentName)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { updateSavedFilter } = useSavedFilterMutations()

  useEffect(() => {
    if (open) setName(currentName)
  }, [open, currentName])

  const handleRename = async () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === currentName) {
      onOpenChange(false)
      return
    }

    setIsSubmitting(true)
    try {
      await updateSavedFilter(filterId, { name: trimmed })
      toast.success(t('savedFilters.toast.renamed'))
      onOpenChange(false)
    } catch {
      toast.error(t('savedFilters.toast.renameFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('savedFilters.renameDialog.title')}</DialogTitle>
          <DialogDescription>{t('savedFilters.renameDialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-2">
          <Label htmlFor="rename-filter">{t('savedFilters.renameDialog.nameLabel')}</Label>
          <Input
            id="rename-filter"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                e.preventDefault()
                void handleRename()
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {tCommon('actions.cancel')}
          </Button>
          <Button
            onClick={() => { void handleRename() }}
            disabled={!name.trim() || isSubmitting}
          >
            {isSubmitting ? t('savedFilters.renameDialog.renaming') : t('savedFilters.renameDialog.rename')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
